// Une seule réverbération à convolution pour toute l'app, montée en bus d'envoi
// comme sur une console de mixage.
//
// Avant, l'app montait jusqu'à six `Tone.Reverb` : la nappe, le piano du manche
// et des leçons, les accords et le bourdon de chaque banque d'échantillons, puis
// le piano et le bourdon du Play de la gamme. Chacune est un ConvolverNode qui
// tourne en continu. La mesure du point 5 de `docs/optimisation-lessons.md`
// donne un quart du temps processeur du panneau pour la seule réverbération du
// piano ; six convolutions coûtent six fois cette mécanique pour un seul son.
//
// Le câblage, source par source :
//
//   source -> bus de mix -+-> gain direct -----------------+-> bus stéréo -> EQ -> sortie
//                         |                                |
//                         +-> gain d'envoi -> réverbération +
//
// Le bus de mix reste le curseur du mixer de l'utilisateur (cf playbackMix). Le
// gain d'envoi est pris APRÈS ce fader : baisser un curseur baisse le direct et
// sa réverbération ensemble, exactement comme avant, où la réverbération était
// en insert devant le bus.
//
// Dosage. `Tone.Reverb` mélange direct et effet par un CrossFade à puissance
// constante : un StereoPannerNode piloté à `pan = 2·wet - 1`, dont la loi donne
// un gain direct `cos(wet·π/2)` et un gain d'effet `sin(wet·π/2)`. Poser ces
// deux gains de part et d'autre du bus reproduit donc exactement le dosage d'un
// `wet`, d'où `equalPowerGains`. Contrôle sur la valeur déjà notée dans
// notePiano : wet 0,35 donne bien un direct à 0,85.
//
// Ce qui change à l'oreille : la durée devient commune (REVERB_DECAY, posée
// avec les autres réglages du mixer dans audioSettings), là où chaque source
// avait la sienne (nappe 5 s, pianos 3 s, guitare 1,8 s, bourdon
// du manche 4 s). Le rapport direct/effet est conservé source par source, et
// l'énergie totale de l'effet aussi : `Tone.Reverb` bâtit son ConvolverNode par
// `createConvolver()` sans toucher à `normalize`, qui vaut vrai par défaut, donc
// la réponse impulsionnelle est normalisée quelle que soit sa durée. Ce qui
// bouge est la répartition dans le temps : la nappe raccourcit et sa queue
// devient plus dense à l'attaque, la guitare et le bourdon du manche s'allongent
// et leur queue s'amincit d'autant. À valider à l'écoute.

import * as Tone from 'tone';
import eqProcessor from './eqProcessor';
import { bypassReverb } from './perfVariant';
import { savedReverbDecay } from './audioSettings';
import { subscribeUser } from '../data/userStore';

// Bus et réverbération sont bâtis une fois pour la vie de la page : les jeter et
// les reconstruire empilerait des nœuds à chaque relance d'une source.
let stereoOut = null;
let reverb = null;
// Nulle tant que personne n'a tranché. La convolution est alors bâtie sur la
// durée sauvegardée par l'utilisateur : la bâtir sur le défaut pour la
// régénérer ensuite coûterait une réponse impulsionnelle pour rien.
let decay = null;

const decaySeconds = () => {
  if (decay === null) decay = savedReverbDecay();
  return decay;
};

// Gains direct et d'envoi équivalents à un `wet` de Tone, au fondu à puissance
// constante près. w = 0 donne tout au direct, w = 1 tout à l'effet.
export const equalPowerGains = (wet) => ({
  dry: Math.cos(wet * Math.PI / 2),
  send: Math.sin(wet * Math.PI / 2),
});

// Bus stéréo commun : tout y arrive, direct et retour d'effet, puis l'égaliseur
// global et la sortie. Les samplers partaient jusqu'ici droit à la destination,
// ils passent désormais par l'égaliseur comme la nappe.
const getStereoOut = () => {
  if (!stereoOut) stereoOut = new Tone.Gain(1).connect(eqProcessor.getInput());
  return stereoOut;
};

// Variantes C et D du point 5 : contournement réel. Aucune convolution n'est
// bâtie et les envois ne sont connectés à rien, là où un `wet` à zéro laisserait
// le ConvolverNode tourner.
const getReverb = () => {
  if (!reverb && !bypassReverb()) {
    reverb = new Tone.Reverb({ decay: decaySeconds(), wet: 1 }).connect(getStereoOut());
  }
  return reverb;
};

// La garantie que l'app attendait par source avant de jouer, tenue par une seule
// promesse. `Tone.Reverb` remplace sa propre `ready` à chaque changement de
// durée, donc on la relit à chaque appel plutôt que d'en figer une à l'import.
export const whenReverbReady = () => {
  const node = getReverb();
  return node ? node.ready : Promise.resolve();
};

// Branche une source (son bus de mix, ou le gain maître de la nappe) sur le bus
// stéréo et sur la réverbération, au dosage de son ancien `wet`.
export const connectReverbSend = (source, wet) => {
  const gains = equalPowerGains(wet);
  const out = getStereoOut();
  const node = getReverb();
  const dry = new Tone.Gain(gains.dry).connect(out);
  const send = new Tone.Gain(gains.send);
  if (node) send.connect(node);
  source.connect(dry);
  source.connect(send);
  return {
    // Le réglage de mix de la nappe pilote désormais ce couple de gains.
    setWet(next) {
      const ramped = equalPowerGains(next);
      dry.gain.rampTo(ramped.dry, 0.03);
      send.gain.rampTo(ramped.send, 0.03);
    },
    // Ne jette que les deux gains de cet envoi. La réverbération et le bus
    // stéréo sont partagés, ils ne se démontent jamais.
    dispose() {
      dry.dispose();
      send.dispose();
    },
  };
};

// Durée commune à toutes les sources. Régénérer la réponse impulsionnelle est
// coûteux, donc une valeur identique ne touche à rien : le mixer repousse la
// sienne à chaque montage.
export const setReverbDecay = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0 || seconds === decaySeconds()) return;
  decay = seconds;
  if (reverb) reverb.decay = seconds;
};

// Le réglage sauvegardé n'arrive parfois qu'après le démarrage : hydratation du
// miroir user, puis synchro du compte à la connexion. On le repousse à chaque
// notification du miroir, comme audioEngine le fait pour le timbre. Rien n'est
// construit ici : sans convolution, la durée l'attend.
subscribeUser(() => setReverbDecay(savedReverbDecay()));
